package diana.soleil.design.iraniancanadienbazzar.core.designsystem.component

/** A pin already projected to screen space (dp, relative to the map's top-left). */
internal data class ScreenPin(val pin: MapPin, val x: Double, val y: Double)

/**
 * One rendered marker: a single pin, or several collapsed into a counted bubble.
 * [members] always holds at least one pin; [x]/[y] is their mean screen position.
 */
internal data class PinCluster(val x: Double, val y: Double, val members: List<MapPin>) {
    val single: MapPin? get() = members.singleOrNull()
    val count: Int get() = members.size
}

/**
 * Collapses pins that land on top of each other into counted bubbles.
 *
 * Why: with real prod coordinates, ~150 GTA businesses at metro zoom merge into a single opaque
 * blob roughly Vaughan-to-Markham wide — no business distinguishable, nothing usefully tappable,
 * and the street grid painted over. Going from an empty map to an ink blot is not progress.
 *
 * Deliberately NOT solved by lowering the pin cap: capping harder thins the blob by hiding real
 * businesses, trading one wrong map for another. Clustering keeps every business reachable — the
 * bubbles split as you zoom in, because the grid is in SCREEN space, so the same coordinates spread
 * across more cells at higher zoom.
 *
 * A [MapPin.selected] pin is never absorbed: the caller has explicitly surfaced it, and hiding it
 * inside a bubble would make the selection disappear.
 *
 * Grid bucketing rather than true clustering is chosen on purpose — it is O(n), stable frame to
 * frame (no jitter as the camera moves), and at these counts the visual difference is nil.
 *
 * Grid cells alone still let two bubbles land almost on top of each other: pins hugging a shared
 * cell border average out to centres a few dp apart in neighbouring cells, and only the topmost
 * bubble renders (seen on prod as a lone "36" standing in for ~150 GTA pins on the first frame).
 * So a second pass merges any two markers whose centres are closer than [mergeDp] — the bubble
 * diameter — until none overlap. Selected pins stay out of it, as above.
 */
internal fun clusterPins(
    items: List<ScreenPin>,
    cellDp: Double,
    mergeDp: Double = cellDp,
): List<PinCluster> {
    if (cellDp <= 0.0) return items.map { PinCluster(it.x, it.y, listOf(it.pin)) }
    val buckets = LinkedHashMap<Long, MutableList<ScreenPin>>()
    val loners = ArrayList<PinCluster>()
    for (item in items) {
        if (item.pin.selected) {
            loners += PinCluster(item.x, item.y, listOf(item.pin))
            continue
        }
        // Pack the two cell indices into one key. +32768 keeps negatives (pins scrolled off the
        // top/left, which are still within the cull margin) from colliding with positive cells.
        val cx = kotlin.math.floor(item.x / cellDp).toLong() + 32768L
        val cy = kotlin.math.floor(item.y / cellDp).toLong() + 32768L
        buckets.getOrPut((cx shl 20) or cy) { ArrayList() } += item
    }
    val out = ArrayList<PinCluster>(buckets.size + loners.size)
    for (group in buckets.values) {
        out += PinCluster(
            x = group.sumOf { it.x } / group.size,
            y = group.sumOf { it.y } / group.size,
            members = group.map { it.pin },
        )
    }
    return mergeOverlapping(out, mergeDp) + loners
}

/**
 * Greedy overlap merge: largest bubble first absorbs every other marker whose centre lies within
 * [mergeDp] of its own (recomputed as it grows), repeated until a pass merges nothing. Strictly
 * closer than [mergeDp], so tangent bubbles are left alone. Deterministic for a given input, which
 * keeps bubbles from flickering between frames. O(k²) on marker count, and k is capped upstream.
 */
private fun mergeOverlapping(clusters: List<PinCluster>, mergeDp: Double): List<PinCluster> {
    if (mergeDp <= 0.0 || clusters.size < 2) return clusters
    val limit = mergeDp * mergeDp
    var current = clusters
    while (true) {
        val sorted = current.sortedByDescending { it.count }
        val taken = BooleanArray(sorted.size)
        val merged = ArrayList<PinCluster>(sorted.size)
        var changed = false
        for (i in sorted.indices) {
            if (taken[i]) continue
            taken[i] = true
            var group = sorted[i]
            for (j in i + 1 until sorted.size) {
                if (taken[j]) continue
                val other = sorted[j]
                val dx = group.x - other.x
                val dy = group.y - other.y
                if (dx * dx + dy * dy < limit) {
                    group = group.absorb(other)
                    taken[j] = true
                    changed = true
                }
            }
            merged += group
        }
        if (!changed) return merged
        current = merged
    }
}

/** Union of two clusters, centred on the member-weighted mean of both. */
private fun PinCluster.absorb(other: PinCluster): PinCluster {
    val total = (count + other.count).toDouble()
    return PinCluster(
        x = (x * count + other.x * other.count) / total,
        y = (y * count + other.y * other.count) / total,
        members = members + other.members,
    )
}
